export type ReleaseAccessStatus = "FULL_ACCESS" | "NO_ACCESS" | "LIMITED_ACCESS";

export interface ReleaseAccessResourceTopic {
  topicId: string;
  status: ReleaseAccessStatus;
};

export interface SnippetResource {
  id: string;
  path: string;
  isInternal: boolean;
  accessStatus: ReleaseAccessStatus;
  title?: string;
  topicsAccess: ReleaseAccessResourceTopic[];
  defaultTopicAccess?: ReleaseAccessStatus | null;
}

export interface ArticleResource {
  id: string;
  title: string;
  url: string;
  external: boolean;
  brand: string | null;
  accessStatus: ReleaseAccessStatus;
  createdAt: string;
  updatedAt: string;
  category?: string;
  topicsAccess: ReleaseAccessResourceTopic[];
  defaultTopicAccess?: ReleaseAccessStatus | null;
}

export interface ResourceAccessUpdate {
  id: string;
  status: ReleaseAccessStatus;
  topicsAccess?: ReleaseAccessResourceTopic[];
  defaultTopicAccess?: ReleaseAccessStatus | null;
}

export interface UpdateSnippetAccessPayload {
  snippets: ResourceAccessUpdate[];
}

export interface UpdateArticleAccessPayload {
  articles: ResourceAccessUpdate[];
}

export const accessStatusToLabel: Record<ReleaseAccessStatus, string> = {
  FULL_ACCESS: "Full Access",
  LIMITED_ACCESS: "Limited",
  NO_ACCESS: "No Access",
};

export const accessStatusOptions: ReleaseAccessStatus[] = [
  "FULL_ACCESS",
  "LIMITED_ACCESS",
  "NO_ACCESS",
];

export const STAGED_TOOLTIP_TEXT = "Applied in training. Goes live when the agent is released.";

export type ArticleSourceType =
  | "zendesk"
  | "notion"
  | "confluence"
  | "gitbook"
  | "document360"
  | "google_drive"
  | "elevio"
  | "unknown";

export function getArticleSource(url: string | undefined | null): ArticleSourceType {
  if (!url) return "unknown";
  const lower = url.toLowerCase();
  if (/zendesk\.\w+/.test(lower)) return "zendesk";
  if (/notion\.\w+/.test(lower)) return "notion";
  if (/atlassian\.\w+/.test(lower) || /confluence\.\w+/.test(lower)) return "confluence";
  if (/gitbook\.\w+/.test(lower)) return "gitbook";
  if (/document360\.\w+/.test(lower)) return "document360";
  if (/docs\.google\.\w+/.test(lower) || /drive\.google\.\w+/.test(lower)) return "google_drive";
  if (/elev\.io/.test(lower) || /elevio\.\w+/.test(lower)) return "elevio";
  return "unknown";
}

export const articleSourceLabel: Record<ArticleSourceType, string> = {
  zendesk: "Zendesk",
  notion: "Notion",
  confluence: "Confluence",
  gitbook: "GitBook",
  document360: "Document360",
  google_drive: "Google Drive",
  elevio: "Elevio",
  unknown: "External",
};

export const articleSourceColor: Record<ArticleSourceType, string> = {
  zendesk: "green",
  notion: "bronze",
  confluence: "blue",
  gitbook: "orange",
  document360: "purple",
  google_drive: "yellow",
  elevio: "teal",
  unknown: "gray",
};
